<template>
  <div class="wombat-table" :class="[name]">
    <wombat-header
      :name="name"
      :columns="columns"
      :width="rowWidth"
      :resize="resize"
      :columnsReorder="columnsReorder"
      @columnsResized="(data) => $emit('columnsResized', data)"
      @columnsReordered="(data) => $emit('columnsReordered', data)"
      @columnSorted="$emit('columnSorted', $event)"
    >
      <div
        :slot="headerCellSpotPresent ? 'header-cell' : 'header-cell-disabled'"
        slot-scope="headerCell"
        class="header-cell"
      >
        <slot name="header-cell" :column="headerCell.column" />
      </div>
    </wombat-header>
    <RecycleScroller
      v-show="scrollbarShown"
      class="scroller virtual-list ps"
      ref="scroller"
      :items="items"
      :item-size="itemHeight"
      :key-field="itemKeyName"
    >
      <slot
        slot-scope="row"
        class="row"
        name="row"
        :columns="columns"
        :item="row.item"
        :height="itemHeight"
      >
        <div class="wombat-overlay">
          <div class="wombat-overlay__second-layer"></div>
          <wombat-row :item="row.item" :columns="columns" :height="itemHeight" :clickable="clickableRows" @rowClicked="rowClicked">
            <div
              :slot="rowCellSpotPresent ? 'row-cell' : 'row-cell-disabled'"
              slot-scope="rowCell"
              class="row-cell"
            >
              <slot name="row-cell" :column="rowCell.column" :item="rowCell.item" />
            </div>
          </wombat-row>
        </div>
      </slot>
      <div slot="after">
        <infinite-loading
          v-if="infiniteLoading && items.length"
          class="infinite-loader"
          force-use-infinite-wrapper=".virtual-list"
          :distance="0"
          @infinite="infiniteHandler"
        />

        <div key="tableLoader">
          <slot name="loader"></slot>
        </div>
      </div>
    </RecycleScroller>
    <slot name='no-results'>
      <div v-show="!scrollbarShown && noResultsMessage" slot='no-results' class="no-result-found">{{ noResultsMessage }}</div>
    </slot>
    <slot name="footer">
      <wombat-footer v-if="footerCellSpotPresent" class="wombat-footer" :width="rowWidth">
        <div
          :slot="footerCellSpotPresent ? 'footer-cell' : 'footer-cell-disabled'"
          slot-scope="footerCell"
          class="footer-cell"
        >
          <slot name="footer-cell" :column="footerCell.column" />
        </div>
      </wombat-footer>
    </slot>
  </div>
</template>

<script>
import PerfectScrollbar from 'perfect-scrollbar';
import 'perfect-scrollbar/css/perfect-scrollbar.css';
import InfiniteLoading from 'vue-infinite-loading';
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

import WombatHeader from './WombatHeader.vue';
import WombatRow from './WombatRow.vue';
import WombatFooter from './WombatFooter.vue';

export default {
  name: 'WombatTable',
  components: {
    WombatRow,
    WombatHeader,
    WombatFooter,
    InfiniteLoading,
    RecycleScroller,
  },
  props: {
    columns: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    itemKeyName: {
      type: String,
      default: 'id',
    },
    itemHeight: {
      default: 50,
    },
    resize: {
      type: Boolean,
      default: true,
    },
    columnsReorder: {
      type: Boolean,
      default: true,
    },
    itemsChunkSize: {
      type: Number,
    },
    infiniteLoading: {
      type: Boolean,
      default: false,
    },
    scrollOnItemsAdding: {
      type: Boolean,
      default: true,
    },
    name: {
      type: String,
      default: null,
    },
    loadingItems: {
      type: Boolean,
      required: true,
    },
    noResultsMessage: {
      type: String,
    },
    clickableRows: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      rowWidth: '100%',
      lastScrollTop: 0,
      lastScrollHeight: 0,
    };
  },
  computed: {
    rowCellSpotPresent() {
      return !!this.$scopedSlots['row-cell'] || !!this.$slots['row-cell'];
    },
    headerCellSpotPresent() {
      return !!this.$scopedSlots['header-cell'] || !!this.$slots['header-cell'];
    },
    footerCellSpotPresent() {
      return !!this.$scopedSlots['footer-cell'] || !!this.$slots['footer-cell'];
    },
    itemsLength() {
      return this.items.length;
    },
    scrollbarShown() {
      return this.items.length || this.infiniteLoading;
    },
  },
  methods: {
    rowClicked(item) {
      if (this.clickableRows) {
        this.$emit('rowClicked', item);
      }
    },
    updateScrollBar() {
      this.$nextTick(() => {
        if (this.scrollbar) {
          this.scrollbar.update();
        } else {
          const selector = this.name ? `.${this.name} .virtual-list` : '.virtual-list';
          const el = document.querySelector(selector);
          this.scrollbar = new PerfectScrollbar(el);
          el.addEventListener('ps-y-reach-end', () => {
            if (!this.loadingItems) {
              this.$emit('bottomReached');
            }
          });
        }
      });
    },
    scrollToFirstInsertedItem() {
      const scrollTop = this.lastScrollTop + this.lastScrollHeight - this.itemHeight;
      this.scrollToPosition(scrollTop);
    },
    scrollToPrependedItem() {
      this.scrollToPosition(0);
    },
    infiniteHandler() {
      const { scrollTop, clientHeight } = this.$refs.scroller.$el;
      this.lastScrollTop = scrollTop;
      this.lastScrollHeight = clientHeight;
    },
    scrollToPosition(position) {
      this.$refs.scroller.$el.scrollTop = position;
    },
  },
  watch: {
    items(next, old) {
      const oldLength = old && old.length;
      const nextLength = next && next.length;
      const itemsPrepended = !!oldLength && !!nextLength && old[0][this.itemKeyName] !== next[0][this.itemKeyName];
      const itemsAppended = !!oldLength && !!nextLength && nextLength > oldLength;

      if (this.scrollOnItemsAdding && itemsPrepended) {
        this.$nextTick(this.scrollToPrependedItem);
      } else if (this.scrollOnItemsAdding && itemsAppended) {
        // $nextTick calls scrolling too early, scroll-list is not updated correctly
        setTimeout(() => this.scrollToFirstInsertedItem());
      }

      this.updateScrollBar();
    },
  },
  mounted() {
    this.updateScrollBar();
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/design-system';

.wombat-overlay {
  --display: none;
  --bg: none;

  &__second-layer {
    width: 100%;
    height: 100%;
    position: fixed;
    z-index: 2;
    display: var(--display);
    background-image: var(--bg);
    pointer-events: none;
  }
}

.virtual-list {
  position: relative;
}

.row-wrapper {
  padding-bottom: $space-3;
}

.vue-recycle-scroller.direction-vertical:not(.page-mode) {
  overflow-y: hidden;
}

.header-cell {
  height: 100%;
}

.no-result-found {
  display: flex;
  justify-content: center;
  opacity: 0.6;
  color: var(--text-muted);
  font-size: $font-size-4xl;
  margin: auto;
  overflow: hidden;
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

.infinite-loading-container.infinite-loader {
  height: 0px;
}

.infinite-status-prompt.infinite-status-prompt {
  height: 0px;
  padding: 0;

  i {
    display: none;
  }
}
</style>
